/**
 * cockpit.js
 * ----------
 * Agregats du cockpit directeur (/api/directeur/*) et reglages.
 *
 * Extrait de `directeurApi.js` (559 lignes, toutes les fonctions ajoutees a la
 * suite). Chaque lot ajoutait ses appels en fin de fichier : deux lots ouverts
 * en meme temps entraient donc systematiquement en conflit sur ce fichier.
 * `directeurApi.js` reste le point d'entree — il re-exporte ces modules — pour
 * qu'aucun ecran n'ait a changer ses imports.
 */
import axiosClient from "../axiosClient";

/** GET /directeur/dashboard/ -> { kpis, attendance_trend, actions_requises, agenda, validations, insights, generated_at } */
export async function getDirecteurDashboard() {
  const { data } = await axiosClient.get("/directeur/dashboard/");
  return data || {};
}

/** GET /directeur/overview/ -> bilan institutionnel (répartition par niveau, signaux, échéances) */
export async function getDirecteurOverview() {
  const { data } = await axiosClient.get("/directeur/overview/");
  return data || {};
}

/** GET /directeur/students/ -> annuaire élèves + stats */
export async function getDirecteurStudents(params = {}) {
  const { data } = await axiosClient.get("/directeur/students/", { params });
  return data || {};
}

/** GET /directeur/staff/ -> annuaire personnel + stats */
export async function getDirecteurStaff(params = {}) {
  const { data } = await axiosClient.get("/directeur/staff/", { params });
  return data || {};
}

/** GET /directeur/attendance/ -> analytique présence */
export async function getDirecteurAttendance(params = {}) {
  const { data } = await axiosClient.get("/directeur/attendance/", { params });
  return data || {};
}

/** GET /directeur/pedagogy/ -> analytique pédagogie / notes */
export async function getDirecteurPedagogy(params = {}) {
  const { data } = await axiosClient.get("/directeur/pedagogy/", { params });
  return data || {};
}

/** GET /directeur/schedule/ -> vue emploi du temps */
export async function getDirecteurSchedule(params = {}) {
  const { data } = await axiosClient.get("/directeur/schedule/", { params });
  return data || {};
}

/** GET /directeur/agenda/ -> agenda + évènements */
export async function getDirecteurAgenda(params = {}) {
  const { data } = await axiosClient.get("/directeur/agenda/", { params });
  return data || {};
}

/** GET /directeur/validations/ -> file unifiée des validations */
export async function getDirecteurValidations(params = {}) {
  const { data } = await axiosClient.get("/directeur/validations/", { params });
  return data || {};
}

/** GET /directeur/compliance/ -> checklist conformité */
export async function getDirecteurCompliance() {
  const { data } = await axiosClient.get("/directeur/compliance/");
  return data || {};
}

/** GET /directeur/settings/ -> paramètres établissement */
export async function getDirecteurSettings() {
  const { data } = await axiosClient.get("/directeur/settings/");
  return data || {};
}

/** GET /directeur/sidebar-counts/ -> compteurs pour les pastilles du menu */
export async function getDirecteurSidebarCounts() {
  const { data } = await axiosClient.get("/directeur/sidebar-counts/");
  return data || {};
}

/**
 * POST /directeur/validations/<source>/<id>/decision/
 * @param {string} source     "absence_personnel" | "justification"
 * @param {number} id         identifiant de l'objet
 * @param {"approve"|"reject"} decision
 * @param {string} [commentaire]
 */
export async function decideDirecteurValidation(source, id, decision, commentaire = "") {
  const { data } = await axiosClient.post(
    `/directeur/validations/${source}/${id}/decision/`,
    { decision, commentaire }
  );
  return data;
}

/** GET /admin/logs/ — journal plateforme scopé à l'établissement (niveau, recherche, pagination). */
export async function getAdminLogs(params = {}) {
  const { data } = await axiosClient.get("/admin/logs/", { params });
  return data || {};
}

/**
 * PATCH /directeur/settings/ — met à jour les champs autorisés de
 * l'établissement (identité, coordonnées, couleurs de thème). Le backend
 * ignore silencieusement tout champ hors liste blanche et journalise la
 * modification dans les logs plateforme.
 */
export async function patchDirecteurSettings(payload) {
  const { data } = await axiosClient.patch("/directeur/settings/", payload);
  return data || {};
}

/** GET /school/calendriers/ — calendriers scolaires de l etablissement. */
export async function getCalendriersScolaires() {
  const { data } = await axiosClient.get("/school/calendriers/");
  return Array.isArray(data) ? data : data?.results || [];
}

/**
 * POST /school/calendriers/importer-auto/ — cree le calendrier officiel des vacances de
 * l etablissement : academie deduite du departement ou du code postal, degre de son type.
 */
export async function importerCalendrierOfficiel(payload = {}) {
  const { data } = await axiosClient.post("/school/calendriers/importer-auto/", payload);
  return data || {};
}
